import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CalendarCheck,
  Users,
  MapPin,
  ChefHat,
  ShieldCheck,
  CreditCard,
  Sparkles,
  Lock,
  Check,
  AlertCircle,
} from "lucide-react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Image } from "@/components/ui/image";
import {
  chefQuoteRequestMutations,
  chefQuoteRequestPublicQueries,
} from "@/domain/chef-quote-request/chef-quote-request.query";
import { getImageUrl, getIpxImageUrl } from "@/lib/image";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/(public)/payer/$token")({
  component: RouteComponent,
});

function RouteComponent() {
  const { token } = Route.useParams();
  const { data: quote, isLoading, isError, error } = useQuery(
    chefQuoteRequestPublicQueries.byToken(token),
  );

  const checkoutMutation = useMutation({
    ...chefQuoteRequestMutations.checkoutByToken(),
    onSuccess: (response) => {
      if (response?.url) {
        window.location.href = response.url;
      }
    },
  });

  if (isLoading) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="h-96 rounded-3xl bg-white border border-border animate-pulse" />
      </section>
    );
  }

  if (isError || !quote) {
    return <ErrorState message={getErrorMessage(error)} />;
  }

  if (quote.isAlreadyPaid) {
    return <AlreadyPaidState chefFirstName={quote.chef.firstName} />;
  }

  if (!quote.isPayable) {
    return (
      <ErrorState message="Cette demande ne peut plus être payée. Le statut a changé." />
    );
  }

  const eventDate = quote.eventDate ? new Date(quote.eventDate) : null;
  const heroImage =
    getImageUrl(quote.menu?.image) ?? "/images/menu_1.webp";
  const avatarSrc =
    getIpxImageUrl(quote.chef.avatar, 150) ?? "/images/avatar_1.webp";
  const totalEuros = (quote.totalCents / 100).toFixed(0);
  const pricePerPersonEuros = (quote.pricePerPersonCents / 100).toFixed(0);

  return (
    <section className="bg-[#fbf6f3] min-h-screen py-10">
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-6"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-full">
            <Sparkles size={11} />
            Finalisation de votre réservation
          </span>
          <h1 className="font-h2 text-dark-blue mt-3 mb-1">
            {quote.firstName}, plus qu'une étape
          </h1>
          <p className="text-sm text-muted-foreground">
            Confirmez le paiement pour bloquer votre date avec{" "}
            <span className="font-semibold text-dark-blue">
              {quote.chef.firstName}
            </span>
          </p>
        </motion.header>

        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="bg-white rounded-3xl border border-border overflow-hidden shadow-sm"
        >
          <div className="relative h-40 overflow-hidden bg-gray-100">
            <Image
              src={heroImage}
              fallbackSrc="/images/menu_1.webp"
              alt={quote.menu?.name ?? "Prestation sur-mesure"}
              layout="fullWidth"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-blue/70 via-dark-blue/20 to-transparent" />
            <div className="absolute left-5 right-5 bottom-4 text-white">
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-90">
                {quote.mode === "preset" ? "Menu signature" : "Sur-mesure"}
              </p>
              <h2 className="text-lg font-bold leading-tight">
                {quote.menu?.name ?? `Avec le chef ${quote.chef.firstName}`}
              </h2>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="flex items-center gap-3 pb-5 border-b border-border">
              <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm shrink-0 bg-white">
                <Image
                  src={avatarSrc}
                  alt={quote.chef.firstName}
                  layout="constrained"
                  width={48}
                  height={48}
                  className="object-cover"
                />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
                  Chef
                </p>
                <p className="text-sm font-bold text-dark-blue">
                  {quote.chef.firstName}
                </p>
                {quote.chef.workingCity && (
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <MapPin size={11} />
                    {quote.chef.workingCity}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SummaryRow
                icon={<CalendarCheck size={16} />}
                label="Date"
                value={
                  eventDate
                    ? format(eventDate, "EEEE d MMMM yyyy", { locale: fr })
                    : "À définir"
                }
                className="capitalize"
              />
              <SummaryRow
                icon={<Users size={16} />}
                label="Convives"
                value={`${quote.guests} pers.`}
              />
              <SummaryRow
                icon={<ChefHat className="w-4 h-4" />}
                label="Occasion"
                value={quote.occasion}
              />
              <SummaryRow
                icon={<MapPin size={16} />}
                label="Lieu"
                value={quote.city}
              />
            </div>

            {quote.chefProposal && (
              <div className="rounded-2xl bg-[#fbf6f3] border border-border p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-2">
                  Proposition du chef
                </p>
                <pre className="whitespace-pre-wrap font-sans text-sm text-dark-blue/90 leading-relaxed">
                  {quote.chefProposal}
                </pre>
              </div>
            )}

            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {pricePerPersonEuros}€ × {quote.guests} pers.
                </span>
                <span className="font-semibold text-dark-blue tabular-nums">
                  {totalEuros}€
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-primary/20">
                <span className="text-sm font-bold text-dark-blue">Total</span>
                <span className="text-2xl font-bold text-primary tabular-nums">
                  {totalEuros}€
                </span>
              </div>
            </div>

            <Button
              size="lg"
              onClick={() => checkoutMutation.mutate({ token })}
              disabled={checkoutMutation.isPending}
              className="w-full bg-primary hover:bg-primary/90 text-white gap-2 h-12 text-sm font-semibold shadow-md shadow-primary/20"
            >
              {checkoutMutation.isPending ? (
                "Redirection vers le paiement..."
              ) : (
                <>
                  <Lock size={15} />
                  Payer {totalEuros}€ maintenant
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <ShieldCheck size={11} className="text-emerald-600" />
                Paiement sécurisé
              </span>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1">
                <CreditCard size={11} className="text-primary" />
                Stripe
              </span>
            </div>

            {quote.expiresAt && (
              <p className="text-[10px] text-muted-foreground italic text-center">
                Lien valable jusqu'au{" "}
                {format(new Date(quote.expiresAt), "d MMMM yyyy 'à' HH'h'mm", {
                  locale: fr,
                })}
              </p>
            )}
          </div>
        </motion.article>
      </div>
    </section>
  );
}

function SummaryRow({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="w-8 h-8 rounded-lg bg-salmon/40 text-primary flex items-center justify-center shrink-0">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p
          className={cn(
            "text-sm font-semibold text-dark-blue truncate",
            className,
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function AlreadyPaidState({ chefFirstName }: { chefFirstName: string }) {
  return (
    <section className="container mx-auto px-4 py-16 max-w-2xl">
      <div className="bg-white rounded-3xl border border-border p-10 text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
          <Check size={28} strokeWidth={2.5} />
        </div>
        <h1 className="font-h3 text-dark-blue mb-2">
          Paiement déjà effectué
        </h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Cette réservation a déjà été payée. {chefFirstName} a reçu votre
          confirmation et préparera votre prestation. Vous avez reçu un email
          avec votre facture.
        </p>
      </div>
    </section>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <section className="container mx-auto px-4 py-16 max-w-2xl">
      <div className="bg-white rounded-3xl border border-border p-10 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-100 text-red-700 flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={28} strokeWidth={2.5} />
        </div>
        <h1 className="font-h3 text-dark-blue mb-2">Lien indisponible</h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {message}
        </p>
      </div>
    </section>
  );
}

function getErrorMessage(error: unknown): string {
  if (!error) return "Lien invalide ou expiré.";
  if (typeof error === "object" && error !== null && "message" in error) {
    const msg = (error as { message?: string }).message;
    if (typeof msg === "string" && msg.includes("410"))
      return "Ce lien de paiement a expiré. Contactez le chef pour relancer la demande.";
    if (typeof msg === "string" && msg.includes("404"))
      return "Lien invalide. Vérifiez l'URL ou demandez un nouveau lien.";
    return msg ?? "Une erreur est survenue.";
  }
  return "Une erreur est survenue.";
}
